/*
 * discount-sort.js
 * ----------------
 * Two storefront sort options that Shopify can't do natively — "Highest to
 * Lowest Discount (in %)" and "(in $)" — driven per product by compare_at_price
 * vs price. The options are injected into the sort <select> in
 * snippets/facet-short.liquid (only on collections small enough to sort in the
 * browser). Here we:
 *
 *   1. Intercept selection of those two values (the native sort list is fixed;
 *      Shopify ignores an unknown sort_by), so the theme's normal section
 *      re-render doesn't run.
 *   2. Fetch the whole collection as a light card feed (templates/
 *      collection.discountsort.liquid, paginated by 250, filters preserved),
 *      read data-discount-pct / data-discount-amt off each card, sort, and
 *      render into #ProductsList with a client-side "Show more".
 *   3. Persist the choice in the URL and re-apply it after a native re-render
 *      (e.g. a filter change) via the theme's collection:rerendered event.
 *
 * Collections above the Liquid cap simply never show the options, so this stays
 * inert there. Ordering matches the native cards because it reuses card-product.
 */
(function () {
  'use strict';

  var PCT = 'discount-pct';
  var AMT = 'discount-amt';
  var CUSTOM = [PCT, AMT];
  var FEED_VIEW = 'discountsort';
  var MAX_FEED_PAGES = 5; // safety net; the real ceiling is gated in Liquid (<= 1000)
  var CLIENT_PAGE = 24; // "Show more" chunk size
  var SHOW_MORE_LABEL = 'Show more';

  function isCustom(v) {
    return CUSTOM.indexOf(v) !== -1;
  }

  var state = {
    token: 0, // bumped per request so superseded fetches are dropped
    columns: [], // sorted .f-column.product-column nodes for the active key
    shown: 0,
    cache: null, // { filterKey, columns: [] } — raw fetched set, reused across pct/amt
  };

  // ---- DOM helpers -----------------------------------------------------
  function grid() {
    return document.getElementById('ProductsList');
  }
  function gridContainer() {
    return document.getElementById('ProductGridContainer');
  }
  function currentUrl() {
    return new URL(window.location.href);
  }
  function urlSortBy() {
    return currentUrl().searchParams.get('sort_by');
  }
  function sortSelects() {
    return Array.prototype.slice.call(document.querySelectorAll('select[name="sort_by"][is="facet-short"]'));
  }

  // Filter portion of the URL (everything except sort/page/section/view),
  // normalized — the cache is keyed on this so a filter change triggers a refetch.
  function filterKey() {
    var params = [];
    currentUrl().searchParams.forEach(function (val, key) {
      if (key === 'sort_by' || key === 'page' || key === 'section_id' || key === 'view') return;
      params.push(key + '=' + val);
    });
    return params.sort().join('&');
  }

  function feedUrl(page) {
    var cur = currentUrl();
    var u = new URL(cur.pathname, window.location.origin);
    cur.searchParams.forEach(function (val, key) {
      if (key === 'sort_by' || key === 'page' || key === 'section_id' || key === 'view') return;
      u.searchParams.append(key, val);
    });
    u.searchParams.set('view', FEED_VIEW);
    if (page > 1) u.searchParams.set('page', String(page));
    return u.toString();
  }

  function setSelectsValue(v) {
    sortSelects().forEach(function (sel) {
      if (!sel.querySelector('option[value="' + v + '"]')) return;
      if (sel.value !== v) sel.value = v;
      // FacetShort sizes the fake select from the selected option; nudge it.
      if (typeof sel.calcSelectWidth === 'function') {
        try {
          sel.calcSelectWidth();
        } catch (e) {}
      }
    });
  }

  function loading(on) {
    document.querySelectorAll('[data-facet-loading]').forEach(function (el) {
      el.classList.toggle('btn--loading', !!on);
    });
    var g = grid();
    if (g) g.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function updateUrl(key, silent) {
    if (silent) return;
    var u = currentUrl();
    u.searchParams.set('sort_by', key);
    u.searchParams.delete('page');
    history.replaceState({}, '', u.toString());
  }

  // ---- fetch the full collection as a card feed ------------------------
  function fetchAll(token) {
    var columns = [];
    var fk = filterKey();

    function fetchPage(page) {
      return fetch(feedUrl(page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          if (token !== state.token) return null; // superseded
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cols = doc.querySelectorAll('.f-column.product-column');
          for (var i = 0; i < cols.length; i++) columns.push(cols[i]);
          var hasNext = !!doc.querySelector('[data-feed-has-next]');
          if (hasNext && page < MAX_FEED_PAGES) return fetchPage(page + 1);
          return columns;
        });
    }

    return fetchPage(1).then(function (result) {
      if (result === null) return null;
      state.cache = { filterKey: fk, columns: columns };
      return columns;
    });
  }

  function ensureColumns(token) {
    if (state.cache && state.cache.filterKey === filterKey()) {
      return Promise.resolve(state.cache.columns);
    }
    return fetchAll(token);
  }

  // ---- sort + render ---------------------------------------------------
  function sortColumns(columns, key) {
    var attr = key === PCT ? 'discountPct' : 'discountAmt';
    return columns
      .map(function (col, i) {
        var card = col.querySelector('.product-card');
        var v = card ? parseInt(card.dataset[attr] || '0', 10) : 0;
        if (isNaN(v)) v = 0;
        return { col: col, v: v, i: i };
      })
      .sort(function (a, b) {
        return b.v - a.v || a.i - b.i; // discount desc, stable by feed order
      })
      .map(function (x) {
        return x.col;
      });
  }

  // Remove whatever follows the grid (native pagination) inside the container.
  function stripAfterGrid() {
    var g = grid();
    if (!g) return;
    var sib = g.nextElementSibling;
    while (sib) {
      var next = sib.nextElementSibling;
      sib.remove();
      sib = next;
    }
  }

  function renderLoadMore() {
    stripAfterGrid();
    if (state.shown >= state.columns.length) return;
    var g = grid();
    if (!g || !g.parentNode) return;
    var nav = document.createElement('nav');
    nav.className = 'pagination text-center';
    nav.setAttribute('data-discount-load-more', '');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--primary';
    var span = document.createElement('span');
    span.textContent = SHOW_MORE_LABEL;
    btn.appendChild(span);
    btn.addEventListener('click', function () {
      renderChunk(false);
    });
    nav.appendChild(btn);
    g.parentNode.appendChild(nav);
  }

  function renderChunk(reset) {
    var g = grid();
    if (!g) return;
    if (reset) {
      g.innerHTML = '';
      state.shown = 0;
    }
    var end = Math.min(state.shown + CLIENT_PAGE, state.columns.length);
    var frag = document.createDocumentFragment();
    for (var i = state.shown; i < end; i++) {
      frag.appendChild(document.importNode(state.columns[i], true));
    }
    g.appendChild(frag);
    state.shown = end;
    renderLoadMore();
  }

  function applySort(key, opts) {
    opts = opts || {};
    if (!grid() || !gridContainer()) return;
    state.token += 1;
    var token = state.token;

    setSelectsValue(key);
    updateUrl(key, opts.silentUrl);
    loading(true);

    ensureColumns(token)
      .then(function (columns) {
        if (columns === null || token !== state.token) return; // superseded / failed
        state.columns = sortColumns(columns, key);
        renderChunk(true);
        loading(false);
      })
      .catch(function (e) {
        loading(false);
        if (window.console && console.warn) console.warn('[discount-sort] failed:', e);
      });
  }

  // ---- wiring ----------------------------------------------------------
  // 1. Intercept the two custom values before the theme's native handler runs.
  document.addEventListener(
    'change',
    function (event) {
      var t = event.target;
      if (!t || t.name !== 'sort_by' || t.getAttribute('is') !== 'facet-short') return;
      if (!isCustom(t.value)) return; // native sort → let the theme handle it
      event.stopImmediatePropagation();
      event.preventDefault();
      applySort(t.value);
    },
    true
  );

  // 2. After a native re-render (filter change, or a mobile form-submit that
  //    carried our sort_by), re-apply the discount sort over the new set.
  //    We never dispatch this event ourselves, so there's no loop.
  document.addEventListener('collection:rerendered', function () {
    var sb = urlSortBy();
    if (!isCustom(sb)) return;
    if (!document.querySelector('select[name="sort_by"] option[value="' + sb + '"]')) return;
    applySort(sb, { silentUrl: true });
  });

  // 3. On load: honor a custom sort in the URL, else the per-collection default
  //    from the metafield (data-default-discount-sort on the select).
  function init() {
    if (!grid()) return;
    var sb = urlSortBy();
    if (isCustom(sb)) {
      if (document.querySelector('select[name="sort_by"] option[value="' + sb + '"]')) {
        applySort(sb, { silentUrl: true });
      }
      return;
    }
    if (!sb) {
      var sel = document.querySelector('select[name="sort_by"][data-default-discount-sort]');
      if (sel) {
        var def = sel.getAttribute('data-default-discount-sort');
        if (isCustom(def)) applySort(def);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
